import {
  fetchTournaments,
  fetchLeagueReview,
  fetchTournamentReview,
  fetchApprovalEmail,
  fetchTournamentRegistration,
  fetchSurveyTournaments,
  fetchTournamentQuestionnaire,
} from './api';
import { queryOptions } from '@tanstack/react-query';
import {
  fetchCampaign,
  fetchClinicAvailability,
  fetchGate,
  fetchEvents,
  fetchEventResponse,
  fetchArchivedEvents,
  fetchManagedEvents,
  fetchTournamentForms,
  fetchEventFormProfile,
  fetchEventFormSeasons,
  fetchEventHistory,
  fetchEventTeams,
  fetchQuestionnaireResponses,
  fetchAdminDropins,
  fetchAttendanceSheets,
  fetchDeclaredSubs,
  fetchDropinHistory,
  fetchDropins,
  fetchMe,
  fetchMemberClinics,
  fetchMemberLeague,
  fetchMemberRequirements,
  fetchRegistration,
  fetchWaivers,
  fetchTeam,
  fetchUpcoming,
  startClinicApplication,
  type ClinicStartMode,
} from './api';
import type { Gate } from '../shared/gates';
import type { QuestionnaireSubject } from '../shared/questionnaires';
import type { ReviewKind } from '../shared/teamReview';
import { loadWaiverSections, type WaiverDocumentId } from '../shared/waivers';

export { failureKindOf } from './api';

const CONTENDED = { staleTime: 30_000 } as const;
const SETTLED = {
  staleTime: 6 * 60 * 60_000,
  gcTime: 6 * 60 * 60_000,
} as const;

export const ME_KEY = ['me'] as const;
export const DROPINS_KEY = ['dropins'] as const;
export const ADMIN_DROPINS_KEY = ['admin', 'dropins'] as const;
export const ATTENDANCE_KEY = ['staff', 'dropins'] as const;

export const meQuery = () =>
  queryOptions({ queryKey: ME_KEY, queryFn: fetchMe });

export const memberLeagueQuery = () =>
  queryOptions({ queryKey: [...ME_KEY, 'league'], queryFn: fetchMemberLeague });

export const upcomingQuery = () =>
  queryOptions({ queryKey: [...ME_KEY, 'upcoming'], queryFn: fetchUpcoming });

export const memberClinicsQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'clinics'],
    queryFn: fetchMemberClinics,
  });

export const memberRequirementsQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'requirements'],
    queryFn: fetchMemberRequirements,
  });

export const registrationQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'registration'],
    queryFn: fetchRegistration,
    ...CONTENDED,
  });

export const teamQuery = (teamId: string) =>
  queryOptions({
    queryKey: ['team', teamId],
    queryFn: () => fetchTeam(teamId),
  });

export const declaredSubsQuery = (season: string) =>
  queryOptions({
    queryKey: ['declared-subs', season],
    queryFn: () => fetchDeclaredSubs(season),
  });

export const dropinsQuery = () =>
  queryOptions({
    queryKey: DROPINS_KEY,
    queryFn: fetchDropins,
    ...CONTENDED,
  });

export const dropinHistoryQuery = () =>
  queryOptions({
    queryKey: [...DROPINS_KEY, 'history'],
    queryFn: fetchDropinHistory,
    ...SETTLED,
  });

export const waiversQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: [...ME_KEY, 'waivers'],
    queryFn: fetchWaivers,
    staleTime: Infinity,
    enabled,
  });

export const waiverSectionsQuery = (documentId: WaiverDocumentId) =>
  queryOptions({
    queryKey: ['waivers', 'sections', documentId],
    queryFn: () => loadWaiverSections(documentId),
    staleTime: Infinity,
  });

export const adminDropinsQuery = () =>
  queryOptions({
    queryKey: ADMIN_DROPINS_KEY,
    queryFn: fetchAdminDropins,
    ...SETTLED,
  });

export const attendanceQuery = () =>
  queryOptions({
    queryKey: ATTENDANCE_KEY,
    queryFn: fetchAttendanceSheets,
    ...SETTLED,
  });

export const tournamentsQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'tournaments'],
    queryFn: fetchTournaments,
    ...CONTENDED,
  });
export const surveyTournamentsQuery = () =>
  queryOptions({
    queryKey: ['staff', 'survey-tournaments'],
    queryFn: fetchSurveyTournaments,
    ...CONTENDED,
  });
export const TEAM_REVIEW_KEY = ['staff', 'team-review'] as const;
export const tournamentReviewQuery = (tournamentId: string) =>
  queryOptions({
    queryKey: [...TEAM_REVIEW_KEY, 'tournament', tournamentId],
    queryFn: () => fetchTournamentReview(tournamentId),
  });
export const leagueReviewQuery = () =>
  queryOptions({
    queryKey: [...TEAM_REVIEW_KEY, 'league'],
    queryFn: fetchLeagueReview,
  });
export const approvalEmailQuery = (kind: ReviewKind, teamId: string) =>
  queryOptions({
    queryKey: [...TEAM_REVIEW_KEY, 'approval-email', kind, teamId],
    queryFn: () => fetchApprovalEmail(kind, teamId),
    staleTime: 0,
    gcTime: 0,
  });
export const eventsQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'events'],
    queryFn: fetchEvents,
    ...CONTENDED,
  });
export const eventResponseQuery = (id: string) =>
  queryOptions({
    queryKey: [...ME_KEY, 'events', id, 'response'],
    queryFn: () => fetchEventResponse(id),
  });
export const tournamentRegistrationQuery = (id: string) =>
  queryOptions({
    queryKey: [...ME_KEY, 'tournaments', id],
    queryFn: () => fetchTournamentRegistration(id),
    refetchOnWindowFocus: 'always',
    refetchInterval: (query) =>
      query.state.data?.survey && !query.state.data.survey.completed
        ? 30_000
        : false,
    ...CONTENDED,
  });

export const campaignQuery = () =>
  queryOptions({
    queryKey: [...ME_KEY, 'campaign'],
    queryFn: fetchCampaign,
    staleTime: 30_000,
  });

/** Starting is idempotent, and a restart must run once per visit, so the result never refetches. */
export const clinicApplicationQuery = (mode: ClinicStartMode) =>
  queryOptions({
    queryKey: ['clinicApplication', mode],
    queryFn: () => startClinicApplication(mode),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

export const gateKey = (gate: Gate) => ['gate', gate] as const;
export const gateQuery = (gate: Gate) =>
  queryOptions({
    queryKey: gateKey(gate),
    queryFn: () => fetchGate(gate),
    staleTime: Infinity,
    retry: false,
  });

export const clinicAvailabilityQuery = () =>
  queryOptions({
    queryKey: ['clinicAvailability'],
    queryFn: fetchClinicAvailability,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

export const managedEventsQuery = () =>
  queryOptions({
    queryKey: ['questionnaires', 'event'],
    queryFn: fetchManagedEvents,
    staleTime: 30_000,
  });
export const archivedEventsQuery = () =>
  queryOptions({
    queryKey: ['questionnaires', 'event', 'archived'],
    queryFn: fetchArchivedEvents,
    staleTime: 5 * 60_000,
  });
export const tournamentFormsQuery = () =>
  queryOptions({
    queryKey: ['questionnaires', 'tournament'],
    queryFn: fetchTournamentForms,
    staleTime: 30_000,
  });
export const questionnaireResponsesQuery = (
  subject: Pick<QuestionnaireSubject, 'kind' | 'id'>,
) =>
  queryOptions({
    queryKey: ['questionnaires', subject.kind, subject.id, 'responses'],
    queryFn: () => fetchQuestionnaireResponses(subject),
    staleTime: 30_000,
  });

export const tournamentQuestionnaireQuery = (id: string, teamId: string) =>
  queryOptions({
    queryKey: ['tournament-questionnaire', id, teamId],
    queryFn: () => fetchTournamentQuestionnaire(id, teamId),
    staleTime: 30_000,
  });

export const eventFormProfileQuery = () =>
  queryOptions({
    queryKey: ['event-form-profile'],
    queryFn: fetchEventFormProfile,
    ...SETTLED,
  });
export const eventTeamsQuery = (id: string) =>
  queryOptions({
    queryKey: ['event-forms', id, 'teams'],
    queryFn: () => fetchEventTeams(id),
    staleTime: 30_000,
  });
export const eventHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['event-forms', id, 'history'],
    queryFn: () => fetchEventHistory(id),
    staleTime: 30_000,
  });
export const eventFormSeasonsQuery = () =>
  queryOptions({
    queryKey: ['event-form-seasons'],
    queryFn: fetchEventFormSeasons,
    ...SETTLED,
  });
